import EditIcon from "@mui/icons-material/Edit";
import { Box, TextField, Tooltip, Typography } from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";

interface EditableRampNameProps {
  name: string;
  onNameChange: (newName: string) => void;
}

export const EditableRampName: React.FC<EditableRampNameProps> = ({
  name,
  onNameChange,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = React.useState(false);
  const [editValue, setEditValue] = React.useState(name);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Sync editValue when name prop changes (e.g., after save/reload)
  React.useEffect(() => {
    if (!isEditing) {
      setEditValue(name);
    }
  }, [name, isEditing]);

  // Focus input when entering edit mode
  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    const trimmedValue = editValue.trim();
    if (trimmedValue && trimmedValue !== name) {
      onNameChange(trimmedValue);
    } else {
      // Revert if empty or unchanged
      setEditValue(name);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(name);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <TextField
        inputRef={inputRef}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        size="small"
        variant="standard"
        placeholder={t("settings.rampsSettings.rampEditor.namePlaceholder")}
        sx={{
          minWidth: 120,
          "& .MuiInputBase-input": {
            fontSize: "0.875rem",
            fontWeight: 500,
            py: 0.25,
          },
        }}
      />
    );
  }

  return (
    <Tooltip title={t("settings.rampsSettings.rampEditor.editName")}>
      <Box
        onClick={handleClick}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
          mx: -0.5,
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
          {name || "Ramp"}
        </Typography>
        <EditIcon sx={{ fontSize: 14, opacity: 0.5 }} />
      </Box>
    </Tooltip>
  );
};
